import { HeartHandshake, TrendingUp, Users2, CalendarClock, Rocket, PieChart, AlertTriangle } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip } from "recharts";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const money = (n) => {
  if (n === null || n === undefined) return "-";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
};

// Presentational summary of founder/team sweat equity. Used by founders and investors.
export const SweatEquityCard = ({ summary, entries = [], history = [], variant = "investor" }) => {
  if (!summary || summary.total_accrued === undefined) return null;
  return (
    <Card className="overflow-hidden rounded-xl border bg-card p-0" data-testid="sweat-equity-card">
      <div className="flex flex-col gap-4 border-b bg-primary/5 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary"><HeartHandshake className="h-6 w-6" /></div>
          <div>
            <div className="text-sm font-medium text-muted-foreground">Founder sweat equity invested</div>
            <div className="h-display text-3xl font-semibold tabular-nums text-foreground" data-testid="sweat-total">{money(summary.total_accrued)}</div>
            <p className="mt-1 max-w-md text-xs text-muted-foreground">
              {variant === "investor"
                ? "Value of below-market compensation the team has forgone to build this company - effectively capital invested through their time."
                : "The capital your team has effectively invested by working below market rate."}
            </p>
          </div>
        </div>
        {summary.runway_months_saved > 0 && (
          <div className="rounded-xl border bg-card px-4 py-3 text-center" data-testid="runway-saved">
            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground"><Rocket className="h-3.5 w-3.5" /> Runway saved</div>
            <div className="h-display text-2xl font-semibold tabular-nums text-primary">{summary.runway_months_saved} mo</div>
            <div className="text-[11px] text-muted-foreground">vs full market pay</div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 divide-x sm:grid-cols-4">
        <div className="p-4 text-center">
          <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground"><CalendarClock className="h-3.5 w-3.5" /> Per month</div>
          <div className="mt-1 text-lg font-semibold tabular-nums text-foreground">{money(summary.monthly_run_rate)}</div>
        </div>
        <div className="p-4 text-center">
          <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground"><TrendingUp className="h-3.5 w-3.5" /> Per year</div>
          <div className="mt-1 text-lg font-semibold tabular-nums text-foreground">{money(summary.annual_run_rate)}</div>
        </div>
        <div className="p-4 text-center">
          <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground"><PieChart className="h-3.5 w-3.5" /> Team equity</div>
          <div className="mt-1 text-lg font-semibold tabular-nums text-foreground">{summary.total_equity_pct ? `${summary.total_equity_pct}%` : "-"}</div>
          {summary.total_equity_pct ? <div className="text-[10px] text-muted-foreground">{summary.vested_equity_pct}% vested</div> : null}
        </div>
        <div className="p-4 text-center">
          <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground"><Users2 className="h-3.5 w-3.5" /> Contributors</div>
          <div className="mt-1 text-lg font-semibold tabular-nums text-foreground">{summary.people}</div>
        </div>
      </div>
      {entries.length > 0 && (
        <div className="border-t p-4">
          <div className="space-y-1.5">
            {entries.map((e) => (
              <div key={e.id} className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm hover:bg-secondary" data-testid={`sweat-row-${e.id}`}>
                <div className="min-w-0">
                  <span className="font-medium text-foreground">{e.person_name}</span>
                  {e.role && <span className="ml-1 text-xs text-muted-foreground">· {e.role}</span>}
                  {e.equity_pct ? <span className="ml-1 text-xs text-primary">· {e.equity_pct}% equity</span> : null}
                  <span className="ml-1 text-xs text-muted-foreground">· {e.months} mo{e.ongoing ? " · ongoing" : ""}</span>
                  {e.pre_cliff ? (
                    <Badge className="ml-1.5 gap-1 border-red-200 bg-red-50 font-normal text-red-700" data-testid={`vest-badge-${e.id}`}><AlertTriangle className="h-3 w-3" /> Pre-cliff · 0% vested</Badge>
                  ) : e.outpacing ? (
                    <Badge className="ml-1.5 gap-1 border-amber-200 bg-amber-50 font-normal text-amber-800" data-testid={`vest-badge-${e.id}`}><AlertTriangle className="h-3 w-3" /> Sweat ahead of vesting · {e.vested_equity_pct}% vested</Badge>
                  ) : null}
                </div>
                <span className="tabular-nums font-medium text-foreground">{money(e.accrued)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {history.length > 1 && (
        <div className="border-t p-4">
          <div className="mb-2 text-xs font-medium text-muted-foreground">Cumulative sweat equity</div>
          <div className="h-32 w-full" data-testid="sweat-history-chart">
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 400, height: 128 }}>
              <AreaChart data={history} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="seFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={44}
                  tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)} />
                <RTooltip formatter={(v) => [money(v), "Invested"]} labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Area type="monotone" dataKey="cumulative" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#seFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </Card>
  );
};
